import path from "ramda/es/path";
import T from "ramda/es/T";
import add from "ramda/es/add";
import converge from "ramda/es/converge";
import gt from "ramda/es/gt";
import ifElse from "ramda/es/ifElse";
import includes from "ramda/es/includes";
import o from "ramda/es/o";
import prop from "ramda/es/prop";
import propOr from "ramda/es/propOr";
import defaultToEmptyObject from "ramda-extension/es/defaultToEmptyObject";
import isNilOrEmpty from "ramda-extension/es/isNilOrEmpty";
import memoizeWithIdentity from "ramda-extension/es/memoizeWithIdentity";
import { createSelector } from 'reselect';
import { REDUCER_NAME } from '../constants';
/**
 * Get object containing both an array of current request cache keys and object with all cached data for each cache key
 *
 * @documented
 * @category Selector
 * @example
 *
 * 	getRequestCache(state)
 *
 */ export const getRequestCache = createSelector(prop(REDUCER_NAME), defaultToEmptyObject);
/**
 * Get an array containing all of currently watched request cache keys.
 *
 * @documented
 * @category Selector
 * @example
 *
 * 	getRequestCacheKeys(state)
 *
 */ export const getRequestCacheKeys = createSelector(getRequestCache, propOr([], 'requestCacheKeys'));
/**
 * Whether request with given key is being cached.
 *
 * @documented
 * @category Selector
 * @example
 *
 * 	getIsRequestCached(state)
 *
 */ export const getIsRequestCached = memoizeWithIdentity((requestCacheKey)=>createSelector(getRequestCacheKeys, includes(requestCacheKey)));
/**
 * Get cache data for request by its key.
 *
 * @documented
 * @category Selector
 * @example
 *
 * 	getRequestByCacheKey(requestCacheKey)(state)
 *
 */ export const getRequestByCacheKey = memoizeWithIdentity((requestCacheKey)=>createSelector(getRequestCache, path([
        'cache',
        requestCacheKey
    ])));
/**
 * Get data prop from request cached by key.
 *
 * @documented
 * @category Selector
 * @example
 *
 * 	getRequestData(requestCacheKey)(state)
 *
 */ export const getRequestData = memoizeWithIdentity((requestCacheKey)=>createSelector(getRequestByCacheKey(requestCacheKey), prop('data')));
const timestampP = prop('timestamp');
/**
 * Get timestamp of when request has been cached.
 *
 * @documented
 * @category Selector
 * @example
 *
 * 	getRequestTimestamp(requestCacheKey)(state)
 *
 */ export const getRequestTimestamp = memoizeWithIdentity((requestCacheKey)=>createSelector(getRequestByCacheKey(requestCacheKey), timestampP));
const staleTimeP = prop('staleTime');
/**
 * Get staleTime property of cached request. Meaning how long should data be taken as valid (not staled).
 *
 * @documented
 * @category Selector
 * @example
 *
 * 	getRequestStaleTime(requestCacheKey)(state)
 *
 */ export const getRequestStaleTime = memoizeWithIdentity((requestCacheKey)=>createSelector(getRequestByCacheKey(requestCacheKey), staleTimeP));
/**
 * Get whether cached request defined by key is staled and there is possibility that data arent valid.
 *
 * @documented
 * @category Selector
 * @example
 *
 * 	getIsRequestStaled(requestCacheKey)(state)
 *
 */ export const getIsRequestStaled = (requestCacheKey)=>createSelector(getRequestByCacheKey(requestCacheKey), ifElse(isNilOrEmpty, T, o(gt(Date.now()), converge(add, [
        timestampP,
        staleTimeP
    ]))));
/**
 * Get body part from request data
 *
 * @documented
 * @category Selector
 * @example
 *
 * 	getRequestDataBody(requestCacheKey)(state)
 *
 */ export const getRequestDataBody = memoizeWithIdentity((requestCacheKey)=>createSelector(getRequestData(requestCacheKey), prop('body')));
/**
 * Get headers part from request data
 *
 * @documented
 * @category Selector
 * @example
 *
 * 	getRequestDataHeaders(requestCacheKey)(state)
 *
 */ export const getRequestDataHeaders = memoizeWithIdentity((requestCacheKey)=>createSelector(getRequestData(requestCacheKey), prop('headers')));
/**
 * Get status part from request data
 *
 * @documented
 * @category Selector
 * @example
 *
 * 	getRequestDataStatus(requestCacheKey)(state)
 *
 */ export const getRequestDataStatus = memoizeWithIdentity((requestCacheKey)=>createSelector(getRequestData(requestCacheKey), prop('status')));
